import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Link2, Globe, ExternalLink, Filter, Download, Loader2,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Calendar,
  CheckCircle, XCircle, BarChart3, Shield
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Select from '../../components/ui/Select';
import { competitorService } from '../../services/competitorService';
import toast from 'react-hot-toast';

interface Backlink {
  id: string;
  sourceUrl: string;
  sourceDomain: string;
  targetUrl: string;
  anchorText: string;
  domainAuthority: number;
  type: 'dofollow' | 'nofollow' | 'ugc' | 'sponsored';
  firstSeen: string;
  lastSeen: string;
  isLost: boolean;
}

interface BacklinksData {
  domain: string;
  totalBacklinks: number;
  referringDomains: number;
  dofollowRatio: number;
  nofollowRatio: number;
  newBacklinks30d: number;
  lostBacklinks30d: number;
  avgDomainAuthority: number;
  backlinks: Backlink[];
  newVsLostHistory: { date: string; new: number; lost: number }[];
  topAnchors: { anchor: string; count: number; percentage: number }[];
  linkTypeDistribution: { type: string; count: number; percentage: number }[];
}

interface Filters {
  type: string;
  minDA: string;
  dateRange: string;
  search: string;
}

const BacklinksPage: React.FC = () => {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [backlinksData, setBacklinksData] = useState<BacklinksData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    type: 'all',
    minDA: '0',
    dateRange: '30',
    search: ''
  });

  const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!domain.trim()) return;

    setIsLoading(true);
    setCurrentPage(1);
    try {
      const response = await competitorService.exploreBacklinks(domain.trim());
      if (response.success && response.data) {
        setBacklinksData(response.data);
      } else {
        toast.error(t('competitors.noDataFound', 'No data found for this domain. Try adding it as a competitor and analyzing.'));
        setBacklinksData(null);
      }
    } catch (error) {
      console.error('Error exploring backlinks:', error);
      toast.error(t('errors.generic'));
      setBacklinksData(null);
    } finally {
      setIsLoading(false);
    }
  }, [domain, t]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredBacklinks = backlinksData?.backlinks.filter(backlink => {
    if (filters.type !== 'all' && backlink.type !== filters.type) return false;
    if (parseInt(filters.minDA) > 0 && backlink.domainAuthority < parseInt(filters.minDA)) return false;
    if (filters.search && !backlink.sourceDomain.toLowerCase().includes(filters.search.toLowerCase()) &&
        !backlink.anchorText.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  }) || [];

  const paginatedBacklinks = filteredBacklinks.slice((currentPage - 1) * 20, currentPage * 20);
  const totalPages = Math.ceil(filteredBacklinks.length / 20);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'dofollow': return 'success';
      case 'nofollow': return 'warning';
      case 'ugc': return 'info';
      case 'sponsored': return 'purple';
      default: return 'gray';
    }
  };

  const handleExport = () => {
    if (!backlinksData) return;

    const csvContent = [
      ['Source URL', 'Source Domain', 'Target URL', 'Anchor Text', 'DA', 'Type', 'First Seen', 'Status'].join(','),
      ...filteredBacklinks.map(bl => [
        bl.sourceUrl,
        bl.sourceDomain,
        bl.targetUrl,
        `"${bl.anchorText}"`,
        bl.domainAuthority,
        bl.type,
        formatDate(bl.firstSeen),
        bl.isLost ? 'Lost' : 'Active'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backlinks-${backlinksData.domain}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(t('common.exportSuccess', 'Exported successfully'));
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {t('nav.sections.backlinks', 'Backlinks')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {t('competitors.backlinksDescription', 'Analyze the backlink profile of any domain')}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <Input
                placeholder={t('competitors.enterDomain', 'Enter a domain (e.g.: example.com)')}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                leftIcon={<Globe className="w-5 h-5" />}
              />
            </div>
            <Button type="submit" disabled={isLoading || !domain.trim()} className="whitespace-nowrap flex-shrink-0">
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {t('common.analyze', 'Analyze')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('competitors.analyzingBacklinks', 'Analyzing backlinks...')}
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      {backlinksData && !isLoading && (
        <>
          {/* Main Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Link2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.totalBacklinks', 'Total Backlinks')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {formatNumber(backlinksData.totalBacklinks)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Globe className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.referringDomains', 'Referring Domains')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {formatNumber(backlinksData.referringDomains)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.dofollowRatio', 'Dofollow')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {backlinksData.dofollowRatio}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="hidden sm:block flex-shrink-0 p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.avgDA', 'Avg DA')}
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                      {backlinksData.avgDomainAuthority}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* New vs Lost Cards */}
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <Card padding={false}>
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="hidden sm:block flex-shrink-0 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                        {t('competitors.newBacklinks', 'New (30 days)')}
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-green-600 dark:text-green-400">
                        +{formatNumber(backlinksData.newBacklinks30d)}
                      </p>
                    </div>
                  </div>
                  <ArrowUpRight className="hidden sm:block flex-shrink-0 w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="hidden sm:block flex-shrink-0 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                        {t('competitors.lostBacklinks', 'Lost (30 days)')}
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-red-600 dark:text-red-400">
                        -{formatNumber(backlinksData.lostBacklinks30d)}
                      </p>
                    </div>
                  </div>
                  <ArrowDownRight className="hidden sm:block flex-shrink-0 w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-6">
            {/* New vs Lost Chart */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('competitors.newVsLost', 'New vs Lost (30 days)')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={backlinksData.newVsLostHistory}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      interval={4}
                      className="text-gray-500"
                    />
                    <YAxis tick={{ fontSize: 12 }} className="text-gray-500" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', borderColor: '#e5e7eb' }}
                    />
                    <Bar
                      dataKey="new"
                      fill="#10B981"
                      name={t('competitors.new', 'New')}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="lost"
                      fill="#EF4444"
                      name={t('competitors.lost', 'Lost')}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Link Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('competitors.linkTypeDistribution', 'Distribution by Type')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <div className="w-full sm:w-1/2">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={backlinksData.linkTypeDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="percentage"
                          nameKey="type"
                        >
                          {backlinksData.linkTypeDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => `${value}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-full sm:w-1/2 space-y-2 sm:space-y-3">
                    {backlinksData.linkTypeDistribution.map((item, index) => (
                      <div key={item.type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {item.type}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.percentage}%
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({formatNumber(item.count)})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Anchors */}
          <Card>
            <CardHeader>
              <CardTitle>
                {t('competitors.topAnchors', 'Top Anchor Texts')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {backlinksData.topAnchors.map((anchor, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg"
                    style={{
                      fontSize: `${Math.max(12, 18 - index)}px`
                    }}
                  >
                    <span className="text-gray-700 dark:text-gray-300">
                      {anchor.anchor}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {formatNumber(anchor.count)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Filters and Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {t('competitors.backlinksList', 'Backlinks List')}
                </CardTitle>
                <Button variant="secondary" size="sm" onClick={handleExport} className="whitespace-nowrap flex-shrink-0">
                  <Download className="w-4 h-4 mr-2" />
                  {t('common.export', 'Export')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-full sm:w-48">
                  <Input
                    placeholder={t('common.search', 'Search...')}
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    leftIcon={<Search className="w-4 h-4" />}
                  />
                </div>
                <div className="w-full sm:w-40">
                  <Select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    options={[
                      { value: 'all', label: t('common.all', 'All') },
                      { value: 'dofollow', label: 'Dofollow' },
                      { value: 'nofollow', label: 'Nofollow' },
                      { value: 'ugc', label: 'UGC' },
                      { value: 'sponsored', label: 'Sponsored' }
                    ]}
                  />
                </div>
                <div className="w-full sm:w-40">
                  <Select
                    value={filters.minDA}
                    onChange={(e) => setFilters({ ...filters, minDA: e.target.value })}
                    options={[
                      { value: '0', label: t('competitors.anyDA', 'Any DA') },
                      { value: '20', label: 'DA 20+' },
                      { value: '40', label: 'DA 40+' },
                      { value: '60', label: 'DA 60+' }
                    ]}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('competitors.sourceUrl', 'Source URL')}
                      </th>
                      <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('competitors.anchorText', 'Anchor Text')}
                      </th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        DA
                      </th>
                      <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('competitors.type', 'Type')}
                      </th>
                      <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('competitors.firstSeen', 'First Seen')}
                      </th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedBacklinks.map((backlink) => (
                      <tr key={backlink.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <div>
                            <a
                              href={backlink.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 text-sm"
                            >
                              <span className="truncate max-w-[150px] sm:max-w-none">{backlink.sourceDomain}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px] sm:max-w-[300px]">
                              {backlink.sourceUrl}
                            </p>
                          </div>
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-gray-700 dark:text-gray-300 text-sm max-w-[100px] sm:max-w-[200px] truncate">
                          {backlink.anchorText}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <Badge
                            variant={backlink.domainAuthority >= 50 ? 'success' : backlink.domainAuthority >= 30 ? 'warning' : 'gray'}
                          >
                            {backlink.domainAuthority}
                          </Badge>
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3">
                          <Badge variant={getTypeColor(backlink.type) as any}>
                            {backlink.type}
                          </Badge>
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(backlink.firstSeen)}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          {backlink.isLost ? (
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                              <XCircle className="w-4 h-4" />
                              {t('competitors.lost', 'Lost')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              {t('competitors.active', 'Active')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('common.showing', 'Showing')} {((currentPage - 1) * 20) + 1}-{Math.min(currentPage * 20, filteredBacklinks.length)} {t('common.of', 'of')} {filteredBacklinks.length}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('common.previous', 'Previous')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('common.next', 'Next')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty State */}
      {!backlinksData && !isLoading && (
        <Card>
          <CardContent className="text-center py-16">
            <Link2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('competitors.analyzeBacklinks', 'Analyze backlinks from any site')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('competitors.backlinksEmptyState', 'Enter a domain above to discover all backlinks, referring domains, anchor texts and more.')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BacklinksPage;
